import html2canvas from 'html2canvas'

export const DOWNLOAD_REQUIRES_EXTERNAL_BROWSER = 'DOWNLOAD_REQUIRES_EXTERNAL_BROWSER'

function sanitizeFileName(value) {
    return String(value || 'certificate')
        .trim()
        .replace(/[\\/:*?"<>|]+/g, '-')
        .replace(/\s+/g, '_')
}

function waitForImages(element) {
    const images = Array.from(element.querySelectorAll('img'))
    return Promise.all(images.map((image) => {
        if (image.complete && image.naturalWidth > 0) return Promise.resolve()
        return new Promise((resolve, reject) => {
            let timeoutId
            const cleanup = () => {
                clearTimeout(timeoutId)
                image.removeEventListener('load', handleLoad)
                image.removeEventListener('error', handleError)
            }
            const handleLoad = () => {
                cleanup()
                resolve()
            }
            const handleError = () => {
                cleanup()
                reject(new Error('Unable to load certificate artwork'))
            }

            image.addEventListener('load', handleLoad)
            image.addEventListener('error', handleError)
            timeoutId = setTimeout(handleError, 15000)

            if (image.complete && image.getAttribute('src') && image.naturalWidth === 0) {
                handleError()
            }
        })
    }))
}

function isEmbeddedBrowser() {
    const userAgent = navigator.userAgent || ''
    const knownEmbeddedBrowser = /MicroMessenger|Weibo|FBAN|FBAV|Instagram|Line\/|\bwv\b/i.test(userAgent)
    const isIOS = /iPhone|iPad|iPod/i.test(userAgent)
    const knownIOSBrowser = /Safari|CriOS|FxiOS|EdgiOS|OPiOS/i.test(userAgent)

    return knownEmbeddedBrowser || (isIOS && !knownIOSBrowser)
}

function assertDirectDownloadSupport() {
    if (isEmbeddedBrowser()) {
        const error = new Error('Please open this page in a browser to download the image.')
        error.code = DOWNLOAD_REQUIRES_EXTERNAL_BROWSER
        throw error
    }
}

function drawQrCodes(renderedCanvas, sourceDocument) {
    const qrImages = Array.from(sourceDocument.querySelectorAll('[data-certificate-qr] img'))
    if (qrImages.length === 0) return renderedCanvas

    const documentRect = sourceDocument.getBoundingClientRect()
    if (documentRect.width <= 0 || documentRect.height <= 0) {
        throw new Error('Unable to locate the certificate QR code')
    }

    const outputCanvas = document.createElement('canvas')
    outputCanvas.width = renderedCanvas.width
    outputCanvas.height = renderedCanvas.height

    const scaleX = outputCanvas.width / documentRect.width
    const scaleY = outputCanvas.height / documentRect.height
    const context = outputCanvas.getContext('2d')

    context.globalAlpha = 1
    context.globalCompositeOperation = 'source-over'
    context.drawImage(renderedCanvas, 0, 0)
    context.imageSmoothingEnabled = false
    qrImages.forEach((image) => {
        const imageRect = image.getBoundingClientRect()
        context.drawImage(
            image,
            (imageRect.left - documentRect.left) * scaleX,
            (imageRect.top - documentRect.top) * scaleY,
            imageRect.width * scaleX,
            imageRect.height * scaleY
        )
    })

    return outputCanvas
}

function canvasToBlob(canvas) {
    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (blob) {
                resolve(blob)
            } else {
                reject(new Error('Unable to create certificate image'))
            }
        }, 'image/png')
    })
}

async function renderDocument(documentElement) {
    const width = Number(documentElement.dataset.exportWidth) || documentElement.scrollWidth
    const height = Number(documentElement.dataset.exportHeight) || documentElement.scrollHeight
    const stage = document.createElement('div')
    const clone = documentElement.cloneNode(true)

    Object.assign(stage.style, {
        position: 'fixed',
        top: '0',
        left: '-100000px',
        width: width + 'px',
        height: height + 'px',
        overflow: 'hidden',
        pointerEvents: 'none',
        background: '#ffffff'
    })
    Object.assign(clone.style, {
        width: width + 'px',
        height: height + 'px',
        margin: '0',
        transform: 'none'
    })

    stage.appendChild(clone)
    document.body.appendChild(stage)

    try {
        await waitForImages(clone)
        const canvas = await html2canvas(clone, {
            scale: 2,
            useCORS: true,
            allowTaint: false,
            backgroundColor: '#ffffff',
            logging: false,
            width,
            height,
            windowWidth: width,
            windowHeight: height
        })
        return drawQrCodes(canvas, documentElement)
    } finally {
        document.body.removeChild(stage)
    }
}

export async function downloadCertificateImages(rootId, baseFileName) {
    assertDirectDownloadSupport()

    const root = document.getElementById(rootId)
    if (!root) throw new Error('Certificate document not found')

    const documents = Array.from(root.querySelectorAll('[data-certificate-document]'))
    if (documents.length === 0) throw new Error('Certificate document not found')

    for (let index = 0; index < documents.length; index++) {
        await waitForImages(documents[index])
        const canvas = await renderDocument(documents[index])
        const blob = await canvasToBlob(canvas)
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        const suffix = documents.length > 1 ? '_' + (index + 1) : ''

        link.href = url
        link.download = sanitizeFileName(baseFileName) + suffix + '.png'
        link.style.display = 'none'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        setTimeout(() => URL.revokeObjectURL(url), 1000)
    }
}
